import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Home, BookOpen, Images, Leaf, UtensilsCrossed, User, Sparkles } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider.jsx';
import { ApiError } from '../api/client.js';
import { portalApi } from '../api/portal.js';
import { env } from '../lib/env.js';
import { FullScreenLoader } from '../components/ui/FullScreenLoader.jsx';
import { FirstPasswordPage } from '../pages/account/portal/FirstPasswordPage.jsx';

const NAV = [
  { to: '/account', end: true, label: 'Home', Icon: Home },
  { to: '/account/diario', label: 'Diario', Icon: BookOpen },
  { to: '/account/galleria', label: 'Galleria', Icon: Images },
  { to: '/account/benessere', label: 'Benessere', Icon: Leaf },
  { to: '/account/piatto', label: 'Piatto', Icon: UtensilsCrossed },
  { to: '/account/assistente', label: 'Assistente', Icon: Sparkles },
  { to: '/account/profilo', label: 'Profilo', Icon: User }
];

/**
 * The warm shell of the customer portal. Loads the owner's customer and pets
 * and shares them with the pages through the router outlet context, along with
 * the selected pet and a reload helper. A header with the owner's name and a
 * friendly bottom navigation frame the experience.
 *
 * If the account is not yet linked to a customer (404), the owner is sent to
 * the claim screen so they can activate access with their invite.
 */
export function PetLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [selectedPetId, setSelectedPetId] = useState(null);

  // Owners provisioned with a temporary password (kiosk self-registration) must
  // set their own before reaching the portal. The customer/pets queries below
  // are gated on this so we never load portal data behind the gate.
  const mustSetPassword = Boolean(user && user.mustChangePassword);

  const customerQuery = useQuery({
    queryKey: ['portal', 'customer'],
    queryFn: () => portalApi.getCustomer(),
    retry: false,
    enabled: !mustSetPassword
  });

  const linked = customerQuery.isSuccess;
  const petsQuery = useQuery({
    queryKey: ['portal', 'pets'],
    queryFn: () => portalApi.getPets(),
    enabled: linked
  });

  const notLinked = customerQuery.isError
    && customerQuery.error instanceof ApiError
    && customerQuery.error.statusCode === 404;

  useEffect(() => {
    if (notLinked) navigate('/account/claim', { replace: true });
  }, [notLinked, navigate]);

  const onLogout = async () => { await logout(); navigate('/login', { replace: true }); };
  const reloadPets = () => queryClient.invalidateQueries({ queryKey: ['portal', 'pets'] });

  // Forced first-password gate takes precedence over everything else.
  if (mustSetPassword) return <FirstPasswordPage />;

  if (customerQuery.isLoading || notLinked || (linked && petsQuery.isLoading)) {
    return <FullScreenLoader />;
  }

  if (customerQuery.isError && !notLinked) {
    return (
      <div className="flex h-screen items-center justify-center bg-portal-bg px-6 text-center">
        <div>
          <p className="text-portal-ink">Non riusciamo a caricare il tuo spazio in questo momento.</p>
          <button
            type="button"
            onClick={() => customerQuery.refetch()}
            className="mt-4 rounded-full bg-portal-brand px-5 py-2 text-sm font-medium text-white hover:bg-portal-brand-hover"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  const customer = customerQuery.data || null;
  const pets = (petsQuery.data && petsQuery.data.items) || [];
  const selectedPet = pets.find((p) => p._id === selectedPetId) || pets[0] || null;
  const greetingName = (customer && customer.firstName) || (user && user.firstName) || '';

  return (
    <div className="flex h-screen flex-col bg-portal-bg text-portal-ink">
      <header className="flex items-center justify-between px-5 pb-2 pt-5 sm:px-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-portal-ink-faint">{env.appName}</p>
          <p className="text-lg font-medium text-portal-ink">
            {greetingName ? `Ciao, ${greetingName}` : 'Il tuo spazio'}
          </p>
        </div>
        <button
          type="button"
          onClick={onLogout}
          className="text-sm text-portal-ink-muted hover:text-portal-brand"
        >
          Esci
        </button>
      </header>

      {/* Multi-pet selector: shown only when the owner has more than one animal. */}
      {pets.length > 1 && (
        <div className="flex gap-2 overflow-x-auto px-5 pb-2 sm:px-8">
          {pets.map((p) => (
            <button
              key={p._id}
              type="button"
              onClick={() => setSelectedPetId(p._id)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition ${
                p._id === selectedPet?._id
                  ? 'bg-portal-brand text-white'
                  : 'bg-portal-card text-portal-ink-muted hover:text-portal-ink'
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>
      )}

      <main className="flex-1 overflow-y-auto px-5 pb-28 pt-2 sm:px-8">
        {/* Keyed on the path so each navigation remounts and replays the
            fade-in, matching the admin and employee shells' page transitions. */}
        <div key={location.pathname} className="animate-fade-in mx-auto w-full max-w-2xl">
          <Outlet context={{ customer, pets, selectedPet, setSelectedPetId, reloadPets }} />
        </div>
      </main>

      <nav className="fixed inset-x-0 bottom-0 border-t border-portal-border bg-portal-card/95 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-stretch justify-around">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex flex-1 flex-col items-center gap-0.5 py-2.5 text-xs font-medium transition ${
                  isActive ? 'text-portal-brand' : 'text-portal-ink-muted hover:text-portal-ink'
                }`
              }
            >
              <item.Icon className="h-5 w-5" strokeWidth={1.75} />
              {item.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
